import { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";

import { useUser } from "MVVM/Hooks/useUser";
import { usePortal } from "MVVM/Hooks/usePortal";
import { UnifiedUpgradeModal } from "MVVM/Components/Modals/UnifiedUpgradeModal";
import { USER_UPDATE_AUTOCLIP } from "MVVM/GraphQL/user";
import { analytics } from "MVVM/Analytics";

const AUTOCLIP_ELIGIBLE_TIERS = ["PRO", "PRO_PLUS", "CREATOR_PLAT", "INTERNAL"];

// Server default when the user hasn't picked a preset (beatles returns null).
export const AUTOCLIP_DEFAULT_THRESHOLD = 50;

// Split out so surfaces that only need the entitlement (the homepage banner)
// can skip mounting the mutation and upsell modal below.
export const useAutoClipEligible = () => {
  const { allstarUser } = useUser();
  const userType = allstarUser?.user?.userGroup?.type;

  return Boolean(userType && AUTOCLIP_ELIGIBLE_TIERS.includes(userType));
};

export const useAutoClip = (source = "clip-settings") => {
  const { allstarUser, setAllstarUser } = useUser();
  const userType = allstarUser?.user?.userGroup?.type;
  const isEligible = useAutoClipEligible();
  const serverEnabled = allstarUser?.user?.autoclip ?? false;
  const serverThreshold =
    allstarUser?.user?.autoclipThreshold ?? AUTOCLIP_DEFAULT_THRESHOLD;
  const [optimisticEnabled, setOptimisticEnabled] = useState(serverEnabled);
  const [optimisticThreshold, setOptimisticThreshold] =
    useState(serverThreshold);

  const {
    portalOpen: upsellOpen,
    setPortalOpen: setUpsellOpen,
    Portal: UpsellPortal,
  } = usePortal(
    <UnifiedUpgradeModal
      featureName="Autocapture your matches"
      upgradeAction="Autocapture your matches"
      requiredTierKey="PRO"
      analyticsSource="Clip Settings Modal"
      onClose={() => setUpsellOpen(false)}
    />,
  );

  useEffect(() => {
    setOptimisticEnabled(serverEnabled);
  }, [serverEnabled]);

  useEffect(() => {
    setOptimisticThreshold(serverThreshold);
  }, [serverThreshold]);

  const [updateUser, { loading }] = useMutation(USER_UPDATE_AUTOCLIP, {
    onCompleted: (data) => {
      const errors = data?.userUpdate?.errors;
      if (errors?.length) {
        setOptimisticEnabled(serverEnabled);
        setOptimisticThreshold(serverThreshold);
        analytics.track("Autocapture Settings - Error", {
          source,
          error: errors[0].message,
        });
        return;
      }

      const viewer = data?.userUpdate?.viewer;
      setAllstarUser((prev: any) => ({
        ...prev,
        user: {
          ...prev.user,
          autoclip: viewer?.autoclip ?? prev.user?.autoclip,
          autoclipThreshold:
            viewer?.autoclipThreshold ?? prev.user?.autoclipThreshold,
        },
      }));
      analytics.track("Autocapture Settings - Success", {
        source,
        enabled: viewer?.autoclip,
        threshold: viewer?.autoclipThreshold,
      });
    },
    onError: (error) => {
      setOptimisticEnabled(serverEnabled);
      setOptimisticThreshold(serverThreshold);
      analytics.track("Autocapture Settings - Error", {
        source,
        error: error.message,
      });
    },
  });

  const showUpsell = (action: string) => {
    analytics.track("Autocapture Settings - Upsell Clicked", {
      source,
      action,
      userTier: userType,
    });
    setUpsellOpen(true);
  };

  const handleToggle = () => {
    if (loading) return;

    if (!isEligible) {
      showUpsell("toggle");
      return;
    }

    const newValue = !optimisticEnabled;
    setOptimisticEnabled(newValue);
    analytics.track("Autocapture Toggle - Toggled", {
      source,
      enabled: newValue,
      previousValue: optimisticEnabled,
    });

    updateUser({ variables: { input: { autoclip: newValue } } });
  };

  const handleThresholdSelect = (threshold: number) => {
    if (loading || threshold === optimisticThreshold) return;

    if (!isEligible) {
      showUpsell("threshold");
      return;
    }

    setOptimisticThreshold(threshold);
    analytics.track("Autocapture Preset - Selected", {
      source,
      threshold,
      previousThreshold: optimisticThreshold,
    });

    updateUser({ variables: { input: { autoclipThreshold: threshold } } });
  };

  return {
    enabled: optimisticEnabled,
    threshold: optimisticThreshold,
    loading,
    isEligible,
    handleToggle,
    handleThresholdSelect,
    upsellOpen,
    UpsellPortal,
  };
};
